import { useId } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * The two flags. They live here rather than inside DirectorConsole so the
 * toggle in the navbar and the one inside the console are visibly the SAME
 * control — a visitor who learns it in one place recognises it in the other.
 */
export function FlagUS({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={Math.round(size * 0.7)}
      viewBox="0 0 28 20"
      aria-hidden="true"
      className="block rounded-[2px]"
    >
      <rect width="28" height="20" fill="#ffffff" />
      {[0, 2, 4, 6].map((row) => (
        <rect key={row} y={(row * 20) / 7} width="28" height={20 / 7} fill="#B22234" />
      ))}
      <rect width="11.2" height={(3 * 20) / 7 + 20 / 7} fill="#3C3B6E" />
      {[2.2, 5.6, 9].map((x) =>
        [2.2, 5.6, 9].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="0.85" fill="#ffffff" />),
      )}
    </svg>
  );
}

export function FlagMX({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={Math.round(size * 0.7)}
      viewBox="0 0 28 20"
      aria-hidden="true"
      className="block rounded-[2px]"
    >
      <rect width="28" height="20" fill="#ffffff" />
      <rect width="9.33" height="20" fill="#006847" />
      <rect x="18.66" width="9.34" height="20" fill="#CE1126" />
      <circle cx="14" cy="10" r="2.6" fill="#8C6D2F" opacity="0.9" />
      <circle cx="14" cy="10" r="1.3" fill="#6B4F1D" />
    </svg>
  );
}

// ENG / ESP, not EN / ES. Owner, 2026-09-10. The two-letter codes were also
// being rewritten by browser page translation: Google Translate reads a Spanish
// page, takes "EN" and "ES" for the Spanish words "en" (in) and "es" (is), and
// shows "IN" / "IS". The group below is marked translate="no" for that reason.
const LANGUAGES = [
  { code: 'en', label: 'ENG', Flag: FlagUS, titleKey: 'directorConsole.ui.speaksEnglish' },
  { code: 'es', label: 'ESP', Flag: FlagMX, titleKey: 'directorConsole.ui.speaksSpanish' },
] as const;

/**
 * Both flags on screen at once, one click to switch — no dropdown to open and
 * nothing to read first. This is the control a Spanish-speaking visitor has to
 * spot in the first second, so it shows the choice instead of hiding it behind
 * a globe icon.
 *
 * `compact` (2026-09-24) is the navbar's slimmer pill: smaller flags and
 * type, tighter padding, so it fits a 320px phone bar beside the logo and the
 * menu button. Both flags and both ENG/ESP labels stay on screen in every
 * variant (owner's order). The active highlight slides between the two.
 */
export function LanguageFlagToggle({
  size,
  compact = false,
  testIdPrefix = 'nav-lang',
  className = '',
}: {
  size?: number;
  compact?: boolean;
  /** data-testid stem; a second toggle on the same screen needs its own. */
  testIdPrefix?: string;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  // One highlight per toggle instance: a shared layoutId would make the pill
  // fly between the navbar's toggle and the menu's.
  const thumbId = useId();
  const reduceMotion = useReducedMotion();
  const flagSize = size ?? (compact ? 15 : 20);

  // i18n.language can be a region tag ('es-MX'); resolvedLanguage is the bundle
  // that actually loaded. Fall back to the base subtag either way so the toggle
  // never highlights the wrong flag.
  const active = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

  const changeLanguage = (code: string) => {
    // i18next's own cache writes 'i18nextLng' for us, but write it explicitly
    // too so the preference survives a detector misconfig.
    void i18n.changeLanguage(code);
    try {
      localStorage.setItem('i18nextLng', code);
    } catch {
      /* storage blocked (private mode): the choice still holds for this visit */
    }
  };

  return (
    <div
      role="group"
      aria-label={t('language.select')}
      // Browser page translation must never touch the control that picks the
      // language. `translate` is the HTML standard; `notranslate` is the class
      // Google Translate has honoured since before the attribute existed.
      translate="no"
      className={`notranslate flex shrink-0 items-center rounded-full border border-white/10 bg-white/[0.04] ${
        compact ? 'gap-0.5 p-[3px]' : 'gap-1 p-1'
      } ${className}`}
    >
      {LANGUAGES.map(({ code, label, Flag, titleKey }) => {
        const isActive = active === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => changeLanguage(code)}
            aria-pressed={isActive}
            title={t(titleKey)}
            data-testid={`${testIdPrefix}-${code}`}
            // Every segment is a full 44px thumb target on touch screens.
            className={`relative flex items-center rounded-full font-bold transition-[color,opacity] duration-200 ${
              compact
                ? // Below 360px (iPhone SE) the pill tightens and the flags drop to
                  // 13px, the one way a 320px bar fits logo, toggle and menu.
                  // The button is 44px tall but the pill still looks 44px overall
                  // (2026-09-24): -my-1 lets the button run over the pill's 3px
                  // padding and 1px border, and the thumb below is inset by the
                  // same 4px, so the tap target grows while the look doesn't.
                  // Mouse screens get the slimmer 32px pill back. A pointer
                  // test, not lg (2026-09-25): an iPad in landscape is 1024px+
                  // wide and still a finger.
                  '-my-1 min-h-[44px] gap-1 px-1.5 text-[11px] tracking-[0.04em] [&_svg]:h-[9px] [&_svg]:w-[13px] min-[360px]:gap-[5px] min-[360px]:px-2 min-[360px]:tracking-[0.06em] min-[360px]:[&_svg]:h-[10.5px] min-[360px]:[&_svg]:w-[15px] [@media(hover:hover)_and_(pointer:fine)]:my-0 [@media(hover:hover)_and_(pointer:fine)]:min-h-[32px]'
                : // The menu sheet's toggle: phones and tablets only, so 44px throughout.
                  'min-h-[44px] gap-1.5 px-3 py-1 text-xs tracking-wide'
            } ${
              isActive
                ? 'text-primary'
                : 'text-muted-foreground opacity-70 hover:opacity-100 hover:text-foreground'
            }`}
          >
            {isActive && (
              <motion.span
                layoutId={`lang-thumb-${thumbId}`}
                aria-hidden="true"
                className={`absolute inset-x-0 rounded-full bg-primary/[0.16] ring-1 ring-inset ring-primary/50 ${
                  compact ? 'inset-y-1 [@media(hover:hover)_and_(pointer:fine)]:inset-y-0' : 'inset-y-0'
                }`}
                transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 520, damping: 40 }}
              />
            )}
            <span className="relative"><Flag size={flagSize} /></span>
            <span className="relative leading-none">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
